import {
  Box,
  Tab,
  Chip,
  Tabs,
  Paper,
  Stack,
  alpha,
  Tooltip,
  useTheme,
  Typography,
  useMediaQuery,
  LinearProgress,
  linearProgressClasses,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { renderFormattedDate } from 'utils';
import { useDataGridLocaleText } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ResponderPerformanceRow } from '../types';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import type { FreshdeskFeedbackListItem } from 'library/types';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { MAX_FRESHDESK_RATING, MIN_FRESHDESK_RATING } from '../constants';
import { TABLE_SCHEMA_PAGE_NAME_ENUM, TICKET_FEEDBACK_STATUS } from 'enums';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { CustomNoRowsOverlay, CustomToolbar, ZebraDataGrid } from 'components';
import { gridClasses, useGridApiRef, type GridColDef } from '@mui/x-data-grid-premium';

type FeedbackTablesProps = {
  items: FreshdeskFeedbackListItem[];
  responderRows: ResponderPerformanceRow[];
  loading: boolean;
};

type PercentageCellProps = {
  value: number;
  formattedValue: string;
};

const tablePanelSx = {
  gridArea: '1 / 1',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
} as const;

const tablePanelsContainerSx = {
  flex: '1 1 auto',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateRows: 'minmax(0, 1fr)',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
} as const;

const tableContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  height: { xs: 'calc(100vh - 104px)', sm: 'calc(100vh - 112px)' },
  '@supports (height: 100dvh)': {
    height: {
      xs: 'calc(100dvh - 104px)',
      sm: 'calc(100dvh - 112px)',
    },
  },
} as const;

const dataGridSx = {
  [`& .${gridClasses.cell}`]: {
    display: 'flex',
    alignItems: 'center',
  },
} as const;

const PercentageCell = ({ value, formattedValue }: PercentageCellProps) => {
  const theme = useTheme();
  const safeValue = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  const tone = safeValue >= 80 ? 'success' : safeValue >= 60 ? 'warning' : 'error';
  const color = theme.palette[tone].main;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.75,
          border: '1px solid',
          borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.34 : 0.2),
          borderRadius: 99,
          bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.14 : 0.07),
        }}>
        <LinearProgress
          aria-hidden="true"
          variant="determinate"
          value={safeValue}
          sx={{
            flex: 1,
            minWidth: 48,
            height: 8,
            borderRadius: 99,
            [`&.${linearProgressClasses.colorPrimary}`]: {
              bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.24 : 0.14),
            },
            [`& .${linearProgressClasses.bar}`]: {
              borderRadius: 99,
              bgcolor: color,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            minWidth: 40,
            color,
            fontWeight: 800,
            lineHeight: 1,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
          }}>
          {formattedValue}
        </Typography>
      </Box>
    </Box>
  );
};

const FeedbackTables = ({ items, responderRows, loading }: FeedbackTablesProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const dataGridLocaleText = useDataGridLocaleText();
  const feedbackApiRef = useGridApiRef();
  const responderApiRef = useGridApiRef();
  const [tab, setTab] = useState(0);
  const [responderTableMounted, setResponderTableMounted] = useState(false);
  const compactTable = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (_: React.SyntheticEvent, nextTab: number) => {
    setTab(nextTab);
    if (nextTab === 1) setResponderTableMounted(true);
  };

  const feedbackColumns = useMemo<GridColDef<FreshdeskFeedbackListItem>[]>(
    () => [
      {
        field: 'ticketId',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.ticketId' }),
        width: 140,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            #{value}
          </Typography>
        ),
      },
      {
        field: 'subject',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.subject' }),
        minWidth: 220,
        flex: 1,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''} enterDelay={500}>
            <Typography variant="body2" tabIndex={0} noWrap>
              {value || intl.formatMessage({ id: 'freshdeskFeedback.notAvailable' })}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'requesterName',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.requester' }),
        minWidth: 175,
        flex: 0.7,
        valueFormatter: (value) => value || intl.formatMessage({ id: 'freshdeskFeedback.notAvailable' }),
      },
      {
        field: 'responderName',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.responder' }),
        minWidth: 175,
        flex: 0.7,
        valueFormatter: (value) => value || intl.formatMessage({ id: 'freshdeskFeedback.unknownResponder' }),
      },
      {
        field: 'rate',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.rating' }),
        width: 125,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => {
          if (typeof value !== 'number' || value < MIN_FRESHDESK_RATING || value > MAX_FRESHDESK_RATING) {
            return intl.formatMessage({ id: 'freshdeskFeedback.notAvailable' });
          }

          const color = value >= 4 ? 'success' : value === 3 ? 'warning' : 'error';
          return (
            <Chip
              size="small"
              icon={<StarRoundedIcon />}
              label={`${value}/${MAX_FRESHDESK_RATING}`}
              color={color}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.status' }),
        width: 135,
        renderCell: ({ value }) => {
          const answered = value === TICKET_FEEDBACK_STATUS.ANSWERED;
          const sent = value === TICKET_FEEDBACK_STATUS.SENT;

          return (
            <Chip
              size="small"
              color={answered ? 'success' : sent ? 'warning' : 'default'}
              variant="outlined"
              label={intl.formatMessage({
                id: answered
                  ? 'freshdeskFeedback.status.answered'
                  : sent
                    ? 'freshdeskFeedback.status.sent'
                    : 'freshdeskFeedback.status.unknown',
              })}
            />
          );
        },
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.description' }),
        minWidth: 280,
        flex: 1.2,
        renderCell: ({ value }) => (
          <Tooltip
            title={value || intl.formatMessage({ id: 'freshdeskFeedback.noDescription' })}
            enterDelay={500}
            placement="top-start">
            <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} tabIndex={0} noWrap>
              {value || intl.formatMessage({ id: 'freshdeskFeedback.noDescription' })}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'updateAt',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.updatedAt' }),
        width: 175,
        type: 'dateTime',
        valueGetter: (value) => (value ? new Date(value) : null),
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
      },
    ],
    [intl]
  );

  const responderColumns = useMemo<GridColDef<ResponderPerformanceRow>[]>(
    () => [
      {
        field: 'responderName',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.responder' }),
        minWidth: 160,
        flex: 1.2,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''} enterDelay={500}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0, fontWeight: 700 }}>
              {value}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'totalRequests',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.totalRequests' }),
        type: 'number',
        width: 92,
      },
      {
        field: 'answeredCount',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.answeredCount' }),
        type: 'number',
        width: 76,
      },
      {
        field: 'responseRate',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.responseRate' }),
        type: 'number',
        headerAlign: 'center',
        minWidth: 142,
        flex: 0.8,
        renderCell: ({ value }) => {
          const percentage = typeof value === 'number' ? value : 0;

          return (
            <PercentageCell
              value={percentage}
              formattedValue={intl.formatNumber(percentage / 100, {
                style: 'percent',
                maximumFractionDigits: 1,
              })}
            />
          );
        },
      },
      {
        field: 'averageRating',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.averageRating' }),
        type: 'number',
        width: 112,
        valueFormatter: (value) => (value ? `${Number(value).toFixed(2)} / ${MAX_FRESHDESK_RATING}` : '—'),
      },
      {
        field: 'satisfactionRate',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.satisfactionRate' }),
        type: 'number',
        headerAlign: 'center',
        minWidth: 146,
        flex: 0.85,
        renderCell: ({ value }) => {
          const percentage = typeof value === 'number' ? value : 0;

          return (
            <PercentageCell
              value={percentage}
              formattedValue={intl.formatNumber(percentage / 100, {
                style: 'percent',
                maximumFractionDigits: 1,
              })}
            />
          );
        },
      },
      {
        field: 'lowRatingCount',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.lowRatingCount' }),
        type: 'number',
        width: 106,
      },
      {
        field: 'latestUpdateAt',
        headerName: intl.formatMessage({ id: 'freshdeskFeedback.columns.lastFeedback' }),
        width: 160,
        type: 'dateTime',
        valueGetter: (value) => (value ? new Date(value) : null),
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
      },
    ],
    [intl]
  );

  return (
    <Paper
      component="section"
      elevation={0}
      sx={{
        ...tableContainerSx,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        scrollMarginTop: { xs: 72, md: 88 },
      }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ px: 2, pt: 2, flexShrink: 0 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            <FormattedMessage id="freshdeskFeedback.tables.title" />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage id="freshdeskFeedback.tables.subtitle" />
          </Typography>
        </Box>
        <Chip
          size="small"
          label={intl.formatMessage(
            { id: tab === 0 ? 'freshdeskFeedback.tables.recordCount' : 'freshdeskFeedback.tables.responderCount' },
            { count: tab === 0 ? items.length : responderRows.length }
          )}
          sx={{ fontWeight: 700 }}
        />
      </Stack>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        aria-label={intl.formatMessage({ id: 'freshdeskFeedback.tables.tabsAriaLabel' })}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ px: 1, mt: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Tab
          id="freshdesk-feedback-tab-0"
          aria-controls="freshdesk-feedback-panel-0"
          icon={<RateReviewOutlinedIcon />}
          iconPosition="start"
          label={<FormattedMessage id="freshdeskFeedback.tables.feedbackTab" />}
        />
        <Tab
          id="freshdesk-feedback-tab-1"
          aria-controls="freshdesk-feedback-panel-1"
          icon={<GroupsOutlinedIcon />}
          iconPosition="start"
          label={<FormattedMessage id="freshdeskFeedback.tables.respondersTab" />}
        />
      </Tabs>

      <Box sx={tablePanelsContainerSx}>
        <Box
          id="freshdesk-feedback-panel-0"
          role="tabpanel"
          aria-labelledby="freshdesk-feedback-tab-0"
          aria-hidden={tab !== 0}
          sx={{
            ...tablePanelSx,
            visibility: tab === 0 ? 'visible' : 'hidden',
            pointerEvents: tab === 0 ? 'auto' : 'none',
            zIndex: tab === 0 ? 1 : 0,
          }}>
          <ZebraDataGrid<FreshdeskFeedbackListItem>
            apiRef={feedbackApiRef}
            rows={items}
            columns={feedbackColumns}
            loading={loading}
            localeText={dataGridLocaleText}
            getRowId={(row) => `${row.ticketId}-${row.responderId}-${row.updateAt || 'unknown'}`}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              sorting: { sortModel: [{ field: 'updateAt', sort: 'desc' }] },
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              columns: {
                columnVisibilityModel: compactTable
                  ? { subject: false, requesterName: false, responderName: false, description: false }
                  : {},
              },
            }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              toolbar: CustomToolbar,
            }}
            slotProps={{
              toolbar: {
                exportPremiumExcel: true,
                showTemplates: true,
                apiRef: feedbackApiRef,
                pageName: TABLE_SCHEMA_PAGE_NAME_ENUM.FRESHDESK_FEEDBACK_REPORT,
              },
            }}
            sx={dataGridSx}
          />
        </Box>

        <Box
          id="freshdesk-feedback-panel-1"
          role="tabpanel"
          aria-labelledby="freshdesk-feedback-tab-1"
          aria-hidden={tab !== 1}
          sx={{
            ...tablePanelSx,
            visibility: tab === 1 ? 'visible' : 'hidden',
            pointerEvents: tab === 1 ? 'auto' : 'none',
            zIndex: tab === 1 ? 1 : 0,
          }}>
          {responderTableMounted && (
            <ZebraDataGrid<ResponderPerformanceRow>
              apiRef={responderApiRef}
              rows={responderRows}
              columns={responderColumns}
              loading={loading}
              localeText={dataGridLocaleText}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                sorting: { sortModel: [{ field: 'averageRating', sort: 'desc' }] },
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                columns: {
                  columnVisibilityModel: compactTable
                    ? { totalRequests: false, answeredCount: false, responseRate: false, latestUpdateAt: false }
                    : {},
                },
              }}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                toolbar: CustomToolbar,
              }}
              slotProps={{
                toolbar: {
                  exportPremiumExcel: true,
                  showTemplates: true,
                  apiRef: responderApiRef,
                  pageName: TABLE_SCHEMA_PAGE_NAME_ENUM.FRESHDESK_FEEDBACK_RESPONDER_PERFORMANCE,
                },
              }}
              sx={dataGridSx}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default FeedbackTables;
